import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getResourceAPI } from "../../redux/slices/resourceSlice";
import { getUIData, setUIData } from "../../redux/slices/uiSlice";
import { fromCustomDate, toCustomDate } from "../../utils/date";
import DateBox from "devextreme-react/date-box";
import SelectBox from "devextreme-react/select-box";
import TextBox from "devextreme-react/text-box";
import NumberBox from "devextreme-react/number-box";
import { Grid, Typography } from "@material-ui/core";
import useResources from "../../hooks/useResources";
import useResourceStatuses from "../../hooks/useResourceStatus";
import Loader from "../generic/Loader";
import CustomSelection from "../generic/CustomSelection";
import { useCallback } from "react";
import { getUserId } from "../../redux/slices/userSlice";

//TODO: change date to european format
//TODO: see why dropdown doesnt scroll at first
//TODO: default value of employee to be logged in user

const TradeCaseEditor = ({ reduxName, pending }) => {
  const dispatch = useDispatch();

  const data = useSelector((state) => getUIData(state, reduxName));
  const userId = useSelector((state) => getUserId(state));

  const [warning, setWarning] = useState("");

  const { vesselStatus, clientEmployeeStatus } = useResourceStatuses();
  const { vessels, clientEmployees, clients } = useResources();

  const handleFieldChange = useCallback(
    (e, name) => {
      let value = e.value;
      if (name === "date") value = toCustomDate(e.value);

      return dispatch(
        setUIData({
          name: reduxName,
          value: { [name]: value },
        })
      );
    },
    [dispatch, reduxName]
  );

  useEffect(() => {
    const { client_ref, vessel_name } = data;
    if (client_ref && vessel_name) {
      dispatch(
        getResourceAPI({
          path: "trade-case/get_client_ref_vessel_name_exists",
          query: { client_ref, vessel_name },
        })
      ).then(({ payload }) => {
        if (payload.data?.exists) {
          setWarning("This combination of client ref and vessel name exists: " + String(payload.data.tc_ids));
        } else {
          setWarning("");
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.client_ref, data.vessel_name, dispatch]);

  useEffect(() => {
    if (!data.client_name) return;

    const foundClientId = clients.find(
      (client) => client.name === data.client_name
    );

    if (!foundClientId) return;

    dispatch(
      getResourceAPI({ path: "vessel", query: { client_id: foundClientId.id } })
    );

    dispatch(
      getResourceAPI({
        path: "client-employee",
        query: { client_id: foundClientId.id },
      })
    );
  }, [clients, data.client_name, dispatch]);

  useEffect(() => {
    if (userId)
      dispatch(
        setUIData({ name: reduxName, value: { employee_name: userId } })
      );

    if (reduxName === "tradeCase:new")
      dispatch(setUIData({ name: reduxName, value: { state: "OPEN" } }));
  }, [dispatch, reduxName, userId]);

  return (
    <>
      {(vesselStatus === "pending" || pending) && (
        <Loader style={{ width: "85%" }} />
      )}
      <div
        style={{
          maxWidth: "600px",
        }}
      >
        <Grid container spacing={1}>
          {warning && (
            <Grid item xs={12}>
              <Typography
                align="center"
                variant="body1"
                style={{ color: "red" }}
              >
                {warning}
              </Typography>
            </Grid>
          )}
          <Grid item xs={6}>
            Date:
          </Grid>
          <Grid item xs={6}>
            <DateBox
              displayFormat="dd/MM/yyyy"
              onValueChanged={(e) => handleFieldChange(e, "date")}
              value={fromCustomDate(data.date)}
            />
          </Grid>
          <Grid item xs={6}>
            Client Ref:
          </Grid>
          <Grid item xs={6}>
            <TextBox
              inputAttr={{ name: "client_ref", autocomplete: "client_ref" }}
              onValueChanged={(e) => handleFieldChange(e, "client_ref")}
              placeholder="Client ref"
              value={data.client_ref}
            />
          </Grid>
          <Grid item xs={6}>
            Client name:
          </Grid>
          <Grid item xs={6}>
            <CustomSelection
              valueExpr="name"
              value={data.client_name}
              resourceName="clients"
              resourcePath="client"
              handleChange={(e) => {
                handleFieldChange(e, "client_name");
              }}
            />
          </Grid>
          <Grid item xs={6}>
            Vessel:
          </Grid>
          <Grid item xs={6}>
            <CustomSelection
              disabled={clientEmployeeStatus !== "done"}
              valueExpr="name"
              value={data.vessel_name}
              dataSource={vessels}
              handleChange={(e) => {
                const { id } = e.component.option("selectedItem");
                handleFieldChange(e, "vessel_name");
                handleFieldChange({ value: id }, "vessel_id");
              }}
            />
          </Grid>
          <Grid item xs={6}>
            Client Employee:
          </Grid>
          <Grid item xs={6}>
            <CustomSelection
              disabled={clientEmployeeStatus !== "done"}
              value={data.client_employee_name}
              dataSource={clientEmployees}
              handleChange={(e) => handleFieldChange(e, "client_employee_name")}
            />
          </Grid>
          <Grid item xs={6}>
            Sales Person:
          </Grid>
          <Grid item xs={6}>
            <CustomSelection
              valueExpr="id"
              value={data.employee_name}
              resourceName="employees"
              resourcePath="employee"
              handleChange={(e) => handleFieldChange(e, "employee_name")}
            />
          </Grid>
          <Grid item xs={6}>
            Status:
          </Grid>
          <Grid item xs={6}>
            <CustomSelection
              value={data.status}
              resourceName="statuses"
              resourcePath="status"
              handleChange={(e) => handleFieldChange(e, "status")}
              displayExpr="status"
              valueExpr="status"
            />
          </Grid>

          <Grid item container spacing={1}>
            <Grid item xs={6}>
              State:
            </Grid>
            <Grid item xs={6}>
              <CustomSelection
                displayExpr="state"
                valueExpr="state"
                value={data.state}
                resourceName="states"
                resourcePath="state"
                handleChange={(e) => handleFieldChange(e, "state")}
              />
            </Grid>
          </Grid>

          <Grid item xs={6}>
            Urgency :
          </Grid>
          <Grid item xs={6}>
            <SelectBox
              onValueChanged={(e) => handleFieldChange(e, "urgency")}
              dataSource={[0, 24, 48, 72]}
              searchEnabled={true}
              value={data.urgency}
            />
          </Grid>
          <Grid item xs={6}>
            Urgent Period :
          </Grid>
          <Grid item xs={6}>
            <NumberBox
              showSpinButtons={true}
              onValueChanged={(e) => handleFieldChange(e, "urgent_period")}
              placeholder="Urgency"
              value={data.urgent_period}
            />
          </Grid>
        </Grid>
      </div>
    </>
  );
};

export default React.memo(TradeCaseEditor);
